import React, { useState, useEffect, useRef } from "react";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  FormControl,
  FormLabel,
  Stack,
  Checkbox,
} from "@chakra-ui/react";
// Main components
import MainLayout from "../../../components/main/layout/layout";
// Maintenance components
import MaintenanceForm from "../../../components/maintenance/form/form";
// Actions
import * as mainActions from "../../../redux/actions/main";
import * as usersActions from "../../../redux/actions/users";
// Utils
import { 
  formatAccessLevels,
} from '../../../utils/formatters';
// Styles
import "./form.css";

const UsersForm = ({ isNew }) => {
  const dispatch = useDispatch();
  const childRef = useRef();
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  // Selectors
  const currentPage = useSelector((state) => state.mainReducer.currentPage);

  // Set constants
  const page = "users";
  const primaryName = !isNew && !!form[`name`] ? form[`name`] : ``;

  // Get id from url
  let id = null;
  const { id: paramId } = useParams();
  if (!isNew) {
    id = parseInt(paramId);
  }

  useEffect(() => {
    if (!!currentPage) return;
    // Set active page
    dispatch(mainActions.setCurrentPage(page));
    // Set initial breadscrum
    if (isNew) {
      const pathKey = 'new';
      const path = `${page}/${pathKey}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey: 'new-m', }]));
    } else {
      const pathKey = 'edit';
      const path = `${page}/${pathKey}/${id}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey, primaryValue: id, primaryName, }]));
    }     
  }, []);

  const handleChange = (evt) => {
    if (childRef.current) {
      childRef.current.handleChange(evt);
    }
  };

  const handleOnChangeAccessLevel = (accessLevelId, checked) => {
    const fieldName = "access_levels";
    if (checked) {
      const currentAccessLevels = !!form[fieldName] ? form[fieldName].split('|') : [];
      let accessLevels = [];
      for (var i = 0; i < currentAccessLevels.length; i++) { 
        const currentAccessLevel = currentAccessLevels[i];
        if (!currentAccessLevel) continue;
        accessLevels.push(currentAccessLevel);
      }
      accessLevels.push(accessLevelId);
      const accessLevelsValue = '|' + accessLevels.join('|') + '|';
      console.log('accessLevelsValue', accessLevelsValue);
      setForm({
        ...form,
        [fieldName]: accessLevelsValue,
      });
    } else {
      let newAccessLevelsValue = form[fieldName].replace(`|${accessLevelId}|`, '|');
      if (newAccessLevelsValue === '|') newAccessLevelsValue = '';
      console.log('newAccessLevelsValue', newAccessLevelsValue);
      setForm({
        ...form,
        [fieldName]: newAccessLevelsValue,
      });
    }
    setIsDirty(true);
  };  

  const renderForm = () => {
    let title = '';
    if (isNew) {
      title = "Nuevo Usuari";
    } else {
      if (!!form[`id`]) {
        title = `${form[`id`]} - ${form[`fullname`]}`;
      }
    }

    // Access levels
    const accessLevels = [1, 2];
    let accessLevelsSelected = [];
    if (!!form['access_levels']) {
      accessLevelsSelected = form['access_levels'].split('|').filter(Boolean);
    }

    return (
      <div className="maintenance-form-card">
        <div className="title maintenance-form-title">
          {title}
        </div>
        <FormControl isRequired>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Email
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Codi"
            name="email"
            data-type={`string`}
            onChange={handleChange}
            value={form[`email`] ? form[`email`] : ``}
            disabled={!isNew}
            type="email"
          />
        </FormControl>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Password
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Password"
            name="password"
            data-type={`string`}
            onChange={handleChange}
            value={form[`password`] ? form[`password`] : ``}
            type="password"
          />
        </FormControl>
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Nom
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Nom"
            name="name"
            data-type={`string`}
            onChange={handleChange}
            value={form[`name`] ? form[`name`] : ``}
          />
        </FormControl>
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Nom complet
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Nom complet"
            name="fullname"
            data-type={`string`}
            onChange={handleChange}
            value={form[`fullname`] ? form[`fullname`] : ``}
          />
        </FormControl>
        {/* IMPORTANT !!! Els checkboxes no poden estar en el mateix FormControl En producció no funciona!! */}
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Nivells d'acces
          </FormLabel>
        </FormControl>
        <Stack 
          spacing={1} 
          className="maintenance-form-checkboxlist"
          minH="80px"
          width="auto"
        >
          {accessLevels.map((accessLevelId, i) => {
            return (
              <Checkbox
                key={`stops-form-accessLevel-checkbox-${i}`}
                isChecked={accessLevelsSelected.includes(accessLevelId.toString())}
                onChange={(e) => {
                  handleOnChangeAccessLevel(accessLevelId.toString(), e.target.checked);
                }}          
              >
                {formatAccessLevels(accessLevelId)}
              </Checkbox>
            );
          })}
        </Stack>   
      </div>   
    );
  };

  return (
    <MainLayout isLoading={isLoading} setIsLoading={setIsLoading}>
      <div id="users-form-content">
        <MaintenanceForm 
          primaryKey={"id"}
          primaryValue={id}
          primaryName={primaryName}
          config={{
            submitFields: [ 'email', 'password', 'name', 'fullname', 'access_levels' ]
          }}
          menuConfig={{}}
          itemNameSingular={"Usuari"}
          actions={usersActions} 
          setIsLoading={setIsLoading}
          renderForm={renderForm}
          form={form}
          setForm={setForm}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          ref={childRef}
          isNew={isNew}
          page={page}
        />
      </div>
    </MainLayout>
  );
};

export default UsersForm;
